import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLoanApplication } from "../../contexts";
import { useDpConfig } from "../../contexts/dpConfig";
import { LoanApplicationStage } from "../../hooks/api/types";
import {
  STAGE_STEP_NAME_MAPPING,
  useStepper,
} from "../../views/LoanApplicationSteps/Stepper";
import {
  normalizeTriggerScreenName,
  screenMatchesAnyTrigger,
} from "../../utils/saasMarketplaceScreenTrigger";
import { SAAS_MARKETPLACE_INTERSTITIAL_OPEN_EVENT } from "../../utils/saasMarketplaceInterstitialEvents";
import { ArrowRightIcon, CloseIcon } from "../../assets/icons";

const isValidHttpUrl = (url: string | undefined): url is string =>
  Boolean(url && (url.startsWith("https://") || url.startsWith("http://")));

const normalizeTriggerScreens = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter(
      (t): t is string => typeof t === "string" && t.length > 0
    );
  }
  if (typeof raw === "string" && raw.trim()) {
    return [raw.trim()];
  }
  return [];
};

const getStagesForMainStepName = (
  mainStepName: string | undefined
): string[] => {
  if (!mainStepName) return [];
  const matches: string[] = [];
  (
    Object.entries(STAGE_STEP_NAME_MAPPING) as [
      LoanApplicationStage,
      [string, number]
    ][]
  ).forEach(([stageKey, [stepName]]) => {
    if (stepName === mainStepName) {
      matches.push(String(stageKey));
    }
  });
  return matches;
};

const SaasMarketplaceInterstitial = () => {
  const {
    state: { settings },
  } = useDpConfig();
  const {
    state: { stage },
  } = useLoanApplication();
  const { steps, currentStep, loading: stepperLoading } = useStepper();
  const cfg = settings.saasMarketplaceBanner;
  const [open, setOpen] = useState(false);

  const interstitialCfg = cfg?.interstitial;
  const configuredImageUrl = interstitialCfg?.imageUrl?.trim();
  const mainStep = steps[currentStep[0]];
  const mainStepName = mainStep?.name;
  const subStepName = mainStep?.subSteps?.[currentStep[1]]?.name;
  const triggers = normalizeTriggerScreens(interstitialCfg?.triggerScreens);
  const hasTriggerScreens = triggers.length > 0;
  const matchesStepperStep = screenMatchesAnyTrigger(
    mainStepName,
    triggers,
    subStepName
  );
  const normalizedTriggerSet = new Set(
    triggers.map((t) => normalizeTriggerScreenName(t))
  );
  const stagesForCurrentScreen = getStagesForMainStepName(mainStepName);
  const matchesScreenStage = stagesForCurrentScreen.some((s) =>
    normalizedTriggerSet.has(normalizeTriggerScreenName(s))
  );
  const matchesPersistedStage = stage
    ? normalizedTriggerSet.has(normalizeTriggerScreenName(String(stage)))
    : false;
  const shouldConsider =
    cfg?.enabled &&
    hasTriggerScreens &&
    Boolean(configuredImageUrl) &&
    (matchesStepperStep || matchesScreenStage || matchesPersistedStage);
  const canOpenManually = Boolean(cfg?.enabled && configuredImageUrl);

  useEffect(() => {
    const handler = () => {
      if (!canOpenManually) return;
      setOpen(true);
    };

    window.addEventListener(SAAS_MARKETPLACE_INTERSTITIAL_OPEN_EVENT, handler);
    return () => {
      window.removeEventListener(
        SAAS_MARKETPLACE_INTERSTITIAL_OPEN_EVENT,
        handler
      );
    };
  }, [canOpenManually]);

  useEffect(() => {
    if (stepperLoading || !steps.length) {
      return;
    }
    if (shouldConsider) {
      setOpen(true);
    }
  }, [shouldConsider, stepperLoading, steps.length]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  const openCta = useCallback(() => {
    const ctaUrl = interstitialCfg?.ctaUrl?.trim();
    if (isValidHttpUrl(ctaUrl)) {
      window.open(ctaUrl, "_blank", "noopener,noreferrer");
    }
    close();
  }, [interstitialCfg?.ctaUrl, close]);

  if (!open || !cfg?.enabled) {
    return null;
  }

  const headline = interstitialCfg?.headline?.trim();
  const subCopy = interstitialCfg?.subCopy?.trim();
  const ctaLabel = interstitialCfg?.ctaLabel?.trim();
  const ctaUrl = interstitialCfg?.ctaUrl?.trim();
  const showCta = Boolean(ctaLabel && isValidHttpUrl(ctaUrl));

  const modal = (
    <div
      className="cx-fixed cx-inset-0 cx-z-[9999] cx-flex cx-items-center cx-justify-center cx-p-4 cx-bg-black/[0.68]"
      role="dialog"
      aria-modal="true"
      aria-label="SaaS marketplace promotion"
      onClick={close}
    >
      <div
        className="cx-relative cx-overflow-hidden cx-rounded-xl cx-shadow-2xl cx-w-[min(962.444px,calc(100vw-32px))] cx-h-[min(600px,calc(100vh-32px))]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="cx-absolute cx-top-3 cx-right-3 cx-z-20 cx-w-5 cx-h-5 cx-flex cx-items-center cx-justify-center cx-rounded-full cx-border cx-border-white cx-text-white hover:cx-bg-black/10"
          aria-label="Close promotion"
        >
          <CloseIcon className="cx-w-3 cx-h-3" />
        </button>
        {configuredImageUrl && (
          <img
            src={configuredImageUrl}
            alt="Exclusive software marketplace promotion"
            className="cx-block cx-w-full cx-h-full cx-object-cover"
          />
        )}
        <div className="cx-absolute cx-inset-x-0 cx-top-0 cx-z-10 cx-flex cx-flex-col cx-items-center cx-text-center cx-pt-10 sm:cx-pt-[58px] cx-px-6 sm:cx-px-12">
          {headline ? (
            <h2
              className="cx-m-0 cx-text-center cx-text-[36px] cx-leading-[52px] cx-font-semibold cx-text-text-primary cx-tracking-[-2.16px] cx-font-['Proxima_Nova',sans-serif] [&_b]:cx-text-[#7C3AED] [&_strong]:cx-text-[#7C3AED]"
              // Headline is managed by trusted DP config and may include inline tags.
              dangerouslySetInnerHTML={{ __html: headline }}
            />
          ) : null}
          {subCopy ? (
            <p className="cx-mt-2 sm:cx-mt-3 cx-max-w-[560px] cx-text-[16px] sm:cx-text-[18px] cx-leading-[1.35] cx-text-text-primary">
              {subCopy}
            </p>
          ) : null}
          {showCta ? (
            <button
              type="button"
              onClick={openCta}
              className="cx-mt-4 sm:cx-mt-5 cx-flex cx-h-12 cx-px-10 cx-py-2 cx-justify-center cx-items-center cx-gap-2 cx-flex-1 cx-basis-0 cx-rounded-full cx-bg-[#0064FF] cx-text-white cx-text-base hover:cx-opacity-95"
            >
              <span>{ctaLabel}</span>
              <ArrowRightIcon className="cx-w-4 cx-h-4" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modal, document.body)
    : null;
};

export default SaasMarketplaceInterstitial;
